import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import WelcomeModal from '../components/WelcomeModal';
import HeroIllustration from '../components/HeroIllustration';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { getCompletedTopicsForDate } from '../utils/quizCompletionCookie';

const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [resetCountdown, setResetCountdown] = useState('--:--:--');
  const [hasPlayedToday, setHasPlayedToday] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useDocumentTitle('Cognidaily | Home');

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const welcomeDismissedKey = `welcome_dismissed_${user.id}`;
    if (localStorage.getItem(welcomeDismissedKey)) return;
    if (user.createdAt) {
      const createdAt = new Date(user.createdAt).getTime();
      const now = Date.now();
      if (createdAt > now - 5 * 60 * 1000) setShowWelcomeModal(true);
    }
  }, [isAuthenticated, user]);

  const handleCloseWelcome = () => {
    setShowWelcomeModal(false);
    if (user) localStorage.setItem(`welcome_dismissed_${user.id}`, 'true');
  };

  useEffect(() => {
    const update = () => {
      const now = Date.now();
      const offsetMs = 10 * 60 * 60 * 1000;
      const msPerDay = 24 * 60 * 60 * 1000;
      const nowInTargetZone = now + offsetMs;
      const startOfDay = Math.floor(nowInTargetZone / msPerDay) * msPerDay;
      const nextMidnight = startOfDay + msPerDay;
      const totalSeconds = Math.floor(Math.max(0, nextMidnight - nowInTargetZone) / 1000);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      setResetCountdown([h, m, s].map((u) => u.toString().padStart(2, '0')).join(':'));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      axios.get('/api/quiz/today').then((r) => {
        setHasPlayedToday(r.data.success && r.data.data?.completed === true);
      }).catch(() => setHasPlayedToday(false));
    } else {
      // Guest: check cookie – all 5 topics completed = played today
      const completed = getCompletedTopicsForDate();
      setHasPlayedToday(completed.length >= 5);
    }
  }, [isAuthenticated]);

  return (
    <>
      <SEOHead
        title="Home"
        description="Daily trivia. 5 topics, 10 questions each. Compete on global and regional leaderboards."
        keywords="trivia, daily quiz, competitive trivia, leaderboard"
        url="https://cognidaily.io/"
      />
      <section className="section section--hero">
        <div className="hero-layout">
          <div className="hero-image">
            <HeroIllustration />
          </div>
          <div className="hero-content">
            <h1>{isAuthenticated && user ? `Hi, ${user.username}` : 'Cognidaily'}</h1>
            <p className="hero-sub">
              {isAuthenticated && hasPlayedToday
                ? 'You played today. Come back tomorrow for new questions.'
                : 'Will you be today\'s champion?'}
            </p>
            <p className="countdown-badge">
              <span className="countdown-badge__label">Today&apos;s time remaining:</span>{' '}
              <span className="countdown-badge__value">{resetCountdown}</span>
            </p>
            <div className="hero-actions">
              {isAuthenticated && hasPlayedToday ? (
                <>
                  <Link to="/leaderboard" className="btn btn-secondary">Leaderboard</Link>
                  <Link to="/profile" className="btn btn-secondary">Profile</Link>
                </>
              ) : (
                <>
                  <Link to="/compete" className="btn btn-primary">Compete</Link>
                  <Link to="/leaderboard" className="btn btn-secondary">Leaderboard</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <p className="hero-tagline">The platform for seriously competitive learners.</p>
      {user && (
        <WelcomeModal isOpen={showWelcomeModal} onClose={handleCloseWelcome} username={user.username} />
      )}
    </>
  );
};

export default Home;
